import styled from "styled-components";
import { Colors } from "../../assets/styles/StyleTypes";
import { ChatDesktopMode } from "../Chat/chatPresentation";
import { CHAT_DESKTOP_PINNED_PANEL_WIDTH } from "../Chat/constants";
const width = "660px";

type ContainerProps = {
  $chatPinned: boolean;
  $sidebarCollapsed: boolean;
};

export const Section = styled.div<{ $chatMode: ChatDesktopMode }>`
  position: relative;
  flex: 1 1 0;

  width: 100%;
  min-width: 0;
  min-height: 0;

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: ${({ $chatMode }) =>
    $chatMode === "page" ? "auto minmax(0, 1fr)" : "minmax(0, 1fr)"};
  grid-template-areas: ${({ $chatMode }) =>
    $chatMode === "page"
      ? `'routed-content' 'chat-surface'`
      : `'routed-content'`};

  overflow-x: hidden;
  overflow-y: ${({ $chatMode }) => ($chatMode === "page" ? "hidden" : "auto")};

  background: ${({ theme }) => theme.colors.background};

  &.chat-page-section {
    display: none;
  }

  @media screen and (max-width: 960px) {
    width: 100dvw;
    display: block;
  }
`;

export const RoutedContent = styled.div<{ $chatMode: ChatDesktopMode }>`
  grid-area: routed-content;
  position: relative;
  z-index: ${({ $chatMode }) => ($chatMode === "page" ? 3 : 0)};
  display: flex;
  width: 100%;
  height: ${({ $chatMode }) => ($chatMode === "page" ? "auto" : "100%")};
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: visible;

  @media screen and (max-width: 960px) {
    height: 100%;
  }
`;

export const ChatPanelAnnouncement = styled.div`
  position: absolute;
  width: 0.0625rem;
  height: 0.0625rem;
  margin: -0.0625rem;
  overflow: hidden;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Header = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 65px;
  width: 100vw;
  height: 65px;
  width: 100dvw;

  background-color: ${(props) => props.theme.colors.header};
  @media screen and (max-width: 960px) {
    display: none;
  }
`;

export const Container = styled.div<ContainerProps>`
  position: relative;
  width: 100%;
  height: 100dvh;
  min-width: 0;
  overflow: hidden;

  display: grid;
  grid-template-columns:
    ${({ $sidebarCollapsed }) => ($sidebarCollapsed ? "100px" : "250px")}
    minmax(0, 1fr)
    ${({ $chatPinned }) => ($chatPinned ? CHAT_DESKTOP_PINNED_PANEL_WIDTH : "0")};

  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: "sidebar main chat-panel";

  background: ${({ theme }) => theme.colors.background};
  transition: grid-template-columns 200ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media screen and (min-width: 961px) {
    &:before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      height: 60px;
      background: ${({ theme }) => theme.colors.header};
      pointer-events: none;
    }
  }

  @media screen and (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) 60px;

    grid-template-areas:
      "main"
      "footer";
  }
`;

export const ShellBackground = styled.div`
  display: contents;
`;

export const SideMenu = styled.div`
  grid-area: sidebar;

  width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  /* z-index: 1; */

  box-shadow: ${(props) => props.theme.colors.navbarShadow};
  border-radius: 0px 35px 35px 0px;

  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  position: relative;

  background: transparent;

  @media screen and (min-width: 961px) {
    z-index: 2;
    background: ${(props) => props.theme.colors.navbar};

    &:before,
    &:after {
      display: none;
    }
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    background: var(--primary-lightest);
    width: 100%;
    height: 65px;
    z-index: 0;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${(props) => props.theme.colors.navbar};
    z-index: 1;
    border-radius: 0px 35px 35px 0px;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media screen and (max-width: 960px) {
    position: absolute;
    top: 0;
    bottom: 60px;
    left: -100%;
    width: 100%;
    height: auto;
    max-height: calc(100dvh - 60px);
    min-height: 0;
    opacity: 1;
    box-shadow: none;
    background: ${Colors.PRIMARY_HIGHLIGHT};
    border-radius: 0px;
    justify-content: center;
    align-items: flex-start;
    transition: all 0.5s ease;

    &:before {
      content: "";
      background: transparent;
    }

    &::after {
      content: "";
      border-radius: 0px;
    }

    &.active {
      left: 0;
      opacity: 1;
      transition: all 0.5s ease;
      z-index: 11;
    }
  }
`;

export const MainArticle = styled.main`
  grid-area: main;

  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;

  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Main = styled.main`
  width: 660px;
  height: 100%;
  position: absolute;
  top: 0px;
  z-index: 1;
  background: ${Colors.PRIMARY_PURE};
  box-shadow: 5px 0px 10px #00000080;
  border-radius: 0px 35px 35px 0px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  overflow: overlay;

  @media only screen and (max-width: 360px) {
    border-radius: 0px;
  }

  @media screen and (max-width: 960px) {
    width: 100vw;
    height: 100vh;
    width: 100dvw;
    height: 100dvh;
    border-radius: 0px;
  }
`;

export const Aside = styled.aside`
  width: calc(100vw - ${width} + 40px);
  //min-height: 750px;
  height: 100%;

  position: absolute;
  top: 0px;
  left: calc(${width} - 40px);
  z-index: 0;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const DivLang = styled.div`
  position: fixed;
  top: 20px;
  left: calc(100vw - 175px);
  height: 40px;
  width: 175px;
  background: #f2f2f2;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 1px 5px #00000080;
  border-radius: 20px 0px 0px 20px;
  opacity: 1;
  overflow-x: none;
  @media (max-width: 960px) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    background: none;
    box-shadow: none;
    margin-right: auto;
    margin-left: auto;
  }
`;

export const Settings = styled.img`
  height: 30px;
  padding: 5px;
  border-radius: 30px;
  border: 2px solid #f15b29;
  background-color: #383838;
  margin: 0px 30px;
`;

export const FooterNav = styled.footer`
  grid-area: footer;
  position: relative;
  z-index: 10;
  width: 100%;
  height: 60px;
  display: none;

  @media screen and (max-width: 960px) {
    display: block;
  }
`;

export const MobileSupportTab = styled.button`
  display: none;

  @media screen and (max-width: 960px) {
    position: fixed;
    right: 0;
    bottom: calc(60px + 2.5dvh + env(safe-area-inset-bottom, 0px));
    z-index: 9;
    display: flex;
    align-items: center;
    justify-content: center;
    width: clamp(1.45rem, 6.2dvw, 1.8rem);
    min-height: clamp(4.7rem, 15dvh, 5.9rem);
    padding: 0.45rem 0.16rem;
    border: 0;
    border-radius: 0.65rem 0 0 0.65rem;
    background: transparent
      linear-gradient(133deg, #4f8fe9 0%, #396bc9 44%, #5e50ae 100%) 0% 0%
      no-repeat padding-box;
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.22);
    color: ${Colors.HIGH_LIGHTEST};
    cursor: pointer;
    font-family: inherit;
    touch-action: manipulation;

    &:focus-visible {
      outline: 3px solid ${Colors.HIGH_LIGHTEST};
      outline-offset: 2px;
    }
  }

  > span:first-child {
    display: block;
    color: inherit;
    font-size: clamp(0.68rem, 2.35dvw, 0.8rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
    white-space: nowrap;
    transform: rotate(-90deg);
    transform-origin: center;
  }
`;
